import { useEffect, useMemo, useRef, type JSX } from "react";
import type { AdminDestinationListItem } from "@ckgo2/shared";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import BatchUploadPanel from "./upload/BatchUploadPanel";
import { uploadAreaVisible, uploadCountriesFromFilter } from "./batchUploadHost";
import { useHostedBatch } from "./useHostedBatch";

/**
 * Stabilní prázdný seznam pro seznam destinací, který se nenačetl ani jednou (nové `[]` každým
 * renderem by měnilo země panelu).
 */
const NO_ROWS: readonly AdminDestinationListItem[] = [];

export interface MediaUploadAreaProps {
  /** Stav tlačítka „Nahrát“ v hlavičce stránky. */
  open: boolean;
  /** `filters.country` stránky (ID země, `"none"`, `"any"` nebo nic). */
  country: string | undefined;
  /** Zavře oblast (stránka: `setUploadOpen(false)`). */
  onClose: () => void;
  /** Po dokončení dávky (a po každé změně jejího koncového stavu) obnoví seznam stránky. */
  onSettled: () => void;
}

/**
 * Oblast nahrávání stránky Média (vize podnety-tymu-2026-10, F7.8): vlastní dávku a hlídání
 * odchodu, panel dávky ukazuje podle tlačítka „Nahrát“ a běhu dávky. Stránka ji vykresluje bez
 * podmínky, takže schování oblasti během nahrávání dávku ani hlídání odchodu nezastaví.
 * Seznam se obnoví jednou po dávce, ne po souborech (obnova během dávky by rušila donačítání
 * mřížky a první stránku stejně čte až konec dávky).
 */
export default function MediaUploadArea({
  open,
  country,
  onClose,
  onSettled,
}: MediaUploadAreaProps): JSX.Element {
  const { batch, guard } = useHostedBatch(onSettled);
  // Týž klíč jako výběr země ve filtrech stránky: požadavek navíc nevzniká. Selhaná obnova na
  // pozadí nechá data v keši, chyba bez předvyplnění platí jen pro seznam bez dat.
  const list = useAdminDestinationList();
  const rows = list.data?.data ?? (list.isError ? NO_ROWS : undefined);
  const initialCountryIds = useMemo(
    () => uploadCountriesFromFilter(country, rows),
    [country, rows],
  );

  // Zavření oblasti hlavičkou mimo běh zruší připravenou dávku (další otevření začne prázdnou
  // zónou); dávka schovaná během běhu zůstane i se souhrnem vidět až do „Hotovo“.
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current && !open && !batch.running) batch.reset();
    prevOpen.current = open;
  }, [open, batch]);

  const closeArea = () => {
    batch.reset();
    onClose();
  };

  // Země panelu musí být při jeho připojení konečné: s konkrétním ID se čeká na seznam
  // destinací (na stránce ho už načítá filtr země, prodleva je v praxi nulová).
  const showPanel = uploadAreaVisible(open, batch) && initialCountryIds !== null;

  return (
    <>
      {showPanel && (
        <BatchUploadPanel
          batch={batch}
          mode="page"
          folder="general"
          initialCountryIds={initialCountryIds}
          onDone={closeArea}
        />
      )}
      {guard.dialog}
    </>
  );
}
